import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Search,
  Filter,
  Star,
  Clock,
  Building,
  MapPin,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Eye,
  RotateCw,
  Sparkles,
  Layers,
  Calendar,
  AlertCircle,
  ShieldAlert,
  Bell,
  History,
  X,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import TenderControlPanel from './components/TenderControlPanel';
import TenderDetailModal from './components/TenderDetailModal';
import TenderRunHistoryModal from './components/TenderRunHistoryModal';
import TenderAlertsModal from './components/TenderAlertsModal';
import {
  getTenderDashboardMetrics,
  getTenderOpportunities,
  getTenderOpportunityById,
  updateTenderCommercialStatus,
  toggleTenderFavorite,
  getTenderAlerts,
} from '../../services/tenderAgentApi';
import { usePermissions } from '../../utils/usePermissions';
import './AppelsOffresDashboard.css';

export default function AppelsOffresDashboard({ onNavigate, onLogout, currentPage = 'appels-offres' }) {
  const { typeUser } = usePermissions();
  const isAdmin = typeUser === 'admin';

  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Opportunities list state
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  // Filters state
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [status, setStatus] = useState('');
  const [tenderType, setTenderType] = useState('');
  const [region, setRegion] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // Modals state
  const [selectedTender, setSelectedTender] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);

  // Initial load
  useEffect(() => {
    fetchMetrics();
    fetchOpportunities(1);
    fetchUnreadAlertsCount();
  }, []);

  // Fetch metrics
  const fetchMetrics = async () => {
    try {
      setMetricsLoading(true);
      const res = await getTenderDashboardMetrics();
      if (res?.success && res.data) {
        setMetrics(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement métriques:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Fetch unread alerts count
  const fetchUnreadAlertsCount = async () => {
    try {
      const res = await getTenderAlerts({ unreadOnly: true, limit: 1 });
      if (res?.success) {
        setUnreadAlertsCount(res.pagination?.total || (res.data ? res.data.length : 0));
      }
    } catch (err) {
      console.error('Erreur comptage alertes:', err);
    }
  };

  // Fetch opportunities
  const fetchOpportunities = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        search: search.trim() || undefined,
        tier: tier || undefined,
        status: status || undefined,
        tenderType: tenderType || undefined,
        region: region || undefined,
        favoritesOnly: favoritesOnly || undefined,
      };

      const res = await getTenderOpportunities(params);
      if (res?.success && res.data) {
        setOpportunities(res.data.opportunities || []);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      }
    } catch (err) {
      console.error('Erreur chargement opportunités:', err);
    } finally {
      setLoading(false);
    }
  }, [search, tier, status, tenderType, region, favoritesOnly]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchOpportunities(1);
  };

  const handleResetFilters = () => {
    setSearch('');
    setTier('');
    setStatus('');
    setTenderType('');
    setRegion('');
    setFavoritesOnly(false);
    setTimeout(() => {
      fetchOpportunities(1);
    }, 0);
  };

  const handleToggleFav = async (tenderId) => {
    try {
      const res = await toggleTenderFavorite(tenderId);
      if (res?.success) {
        setOpportunities((prev) =>
          prev.map((opp) =>
            opp._id === tenderId ? { ...opp, isFavorite: res.data?.isFavorite } : opp
          )
        );
        fetchMetrics();
      }
    } catch (err) {
      console.error('Erreur toggle favori:', err);
    }
  };

  const handleStatusChange = async (tenderId, newStatus) => {
    try {
      const res = await updateTenderCommercialStatus(tenderId, { internalStatus: newStatus });
      if (res?.success) {
        setOpportunities((prev) =>
          prev.map((opp) =>
            opp._id === tenderId ? { ...opp, internalStatus: newStatus } : opp
          )
        );
        fetchMetrics();
      }
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
    }
  };

  const handleOpenTenderDetailById = async (tenderId) => {
    try {
      const res = await getTenderOpportunityById(tenderId);
      if (res?.success && res.data) {
        setSelectedTender(res.data);
      }
    } catch (err) {
      console.error('Erreur récupération détail marché:', err);
    }
  };

  // Helper date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  };

  // Helper badge date limite
  const getDeadlineBadge = (deadlineStr) => {
    if (!deadlineStr) return <span className="text-gray-400 text-xs">Non précisée</span>;
    const now = new Date();
    const deadline = new Date(deadlineStr);
    const diffDays = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <span className="ao-badge ao-badge-closed">Clôturé</span>;
    }
    if (diffDays <= 3) {
      return (
        <span className="ao-badge ao-badge-urgent">
          ⚠️ J-{diffDays}
        </span>
      );
    }
    if (diffDays <= 7) {
      return <span className="ao-badge ao-badge-warning">J-{diffDays}</span>;
    }
    return <span className="ao-badge ao-badge-success">{diffDays} j restants</span>;
  };

  // Helper tier badge
  const getTierBadge = (tierCode, score) => {
    switch (tierCode) {
      case 'HIGH_RELEVANCE':
        return <span className="ao-tier-badge high">{score} • Forte</span>;
      case 'MEDIUM_RELEVANCE':
        return <span className="ao-tier-badge medium">{score} • Moyenne</span>;
      case 'UNCERTAIN':
        return <span className="ao-tier-badge uncertain">{score} • À examiner</span>;
      case 'LOW_RELEVANCE':
      case 'NOT_RELEVANT':
      default:
        return <span className="ao-tier-badge low">{score} • Faible</span>;
    }
  };

  // Sécurité supplémentaire
  if (!isAdmin) {
    return (
      <div className="rh-dashboard-container ao-dashboard-root">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <div className="rh-dashboard-content ao-dashboard-content">
          <div className="ao-access-denied-box">
            <ShieldAlert size={48} className="text-red-500 mb-3" />
            <h2>Accès Strictement Restreint</h2>
            <p>
              Le module de veille des appels d'offres et marchés publics est strictement privé et confidentiel.
              Seuls les administrateurs de Sastra Energy sont habilités à consulter cette rubrique.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = Boolean(search || tier || status || tenderType || region || favoritesOnly);

  return (
    <div className="rh-dashboard-container ao-dashboard-root">
      {/* ─── Menu latéral Back-Office ─── */}
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      {/* ─── Contenu Principal du Module ─── */}
      <div className="rh-dashboard-content ao-dashboard-content">
        {/* ─── 1. Hero Banner Sastra Energy ─── */}
        <section className="rh-hero ao-hero">
          <div className="rh-hero-info ao-hero-info">
            <div className="rh-hero-icon ao-hero-icon">
              <FileText color="white" size={32} />
            </div>
            <div className="rh-hero-text ao-hero-text">
              <div className="ao-hero-title-row">
                <h1>Appels d'Offres & Marchés Publics</h1>
                <span className="ao-confidential-pill">Strictement Confidentiel</span>
              </div>
              <p>
                Veille proactive TUNEPS, qualification technique multilingue (FR/AR) et pipeline commercial pour les opportunités photovoltaïques Sastra Energy.
              </p>
            </div>
          </div>

          <div className="ao-hero-actions">
            {/* Bouton Alertes avec compteur */}
            <button
              type="button"
              className="ao-hero-btn ao-hero-btn-alerts"
              onClick={() => setAlertsOpen(true)}
              title="Centre des alertes d'échéances et de rectificatifs"
            >
              <Bell size={16} />
              <span>Alertes</span>
              {unreadAlertsCount > 0 && (
                <span className="ao-hero-alert-badge">{unreadAlertsCount}</span>
              )}
            </button>

            {/* Bouton Historique des runs */}
            <button
              type="button"
              className="ao-hero-btn"
              onClick={() => setHistoryOpen(true)}
              title="Historique des collectes et des coûts"
            >
              <History size={16} />
              <span>Historique</span>
            </button>

            {/* Bouton Rafraîchir */}
            <button
              type="button"
              className="ao-hero-refresh-btn"
              onClick={() => {
                fetchMetrics();
                fetchOpportunities(pagination.page);
                fetchUnreadAlertsCount();
              }}
              disabled={loading}
              title="Rafraîchir les données"
            >
              <RotateCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </section>

        {/* ─── 2. Supervision du Tender Agent IA ─── */}
        <TenderControlPanel
          onTendersUpdated={() => {
            fetchMetrics();
            fetchOpportunities(1);
            fetchUnreadAlertsCount();
          }}
          onOpenHistory={() => setHistoryOpen(true)}
          onOpenAlerts={() => setAlertsOpen(true)}
          unreadAlertsCount={unreadAlertsCount}
        />

        {/* ─── 3. Grille des 6 Cartes KPI Sastra ─── */}
        <div className="ao-kpi-grid">
          {/* Total Marchés Découverts */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap blue">
              <FileText size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num">{metrics?.totalOpportunities || 0}</span>
              <span className="ao-kpi-title">Marchés Découverts</span>
              <span className="ao-kpi-sub">Total indexé sur TUNEPS</span>
            </div>
          </div>

          {/* Forte Pertinence */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap emerald">
              <Sparkles size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num">{metrics?.highRelevanceCount || 0}</span>
              <span className="ao-kpi-title">Forte Pertinence</span>
              <span className="ao-kpi-sub">Score technique ≥ 70/100</span>
            </div>
          </div>

          {/* À Examiner */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap amber">
              <HelpCircle size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num">{metrics?.uncertainCount || 0}</span>
              <span className="ao-kpi-title">À Examiner</span>
              <span className="ao-kpi-sub">Besoins hybrides (40-69)</span>
            </div>
          </div>

          {/* Marchés Ouverts */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap purple">
              <TrendingUp size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num">{metrics?.openMarketsCount || 0}</span>
              <span className="ao-kpi-title">Marchés Ouverts</span>
              <span className="ao-kpi-sub">Soumissions en cours</span>
            </div>
          </div>

          {/* Échéance Imminente */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap rose">
              <Clock size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num text-red-600">{metrics?.urgentDeadlinesCount || 0}</span>
              <span className="ao-kpi-title">Échéance &lt; 7 Jours</span>
              <span className="ao-kpi-sub text-red-600 font-semibold">Action commerciale requise</span>
            </div>
          </div>

          {/* Marchés Favoris */}
          <div className="ao-kpi-card">
            <div className="ao-kpi-icon-wrap yellow">
              <Star size={22} />
            </div>
            <div className="ao-kpi-details">
              <span className="ao-kpi-num">{metrics?.favoritesCount || 0}</span>
              <span className="ao-kpi-title">Marchés Suivis</span>
              <span className="ao-kpi-sub">Favoris de l'équipe</span>
            </div>
          </div>
        </div>

        {/* ─── 4. Barre de Recherche & Filtres Avancés ─── */}
        <div className="ao-filters-card">
          <form onSubmit={handleFilterSubmit} className="ao-filters-form">
            {/* Barre de recherche */}
            <div className="ao-search-wrap">
              <Search size={16} className="ao-search-icon" />
              <input
                type="text"
                className="ao-search-input"
                placeholder="Rechercher par N° appel d'offres, acheteur public, mot-clé, région..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="ao-search-clear-btn"
                  onClick={() => {
                    setSearch('');
                    setTimeout(() => fetchOpportunities(1), 0);
                  }}
                  title="Effacer la recherche"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Menu de filtres déroulants */}
            <div className="ao-selects-wrap">
              {/* Filtre Tier IA */}
              <select
                className="ao-filter-select"
                value={tier}
                onChange={(e) => {
                  setTier(e.target.value);
                  setTimeout(() => fetchOpportunities(1), 0);
                }}
              >
                <option value="">Tous les Tiers IA</option>
                <option value="HIGH_RELEVANCE">Forte Pertinence (≥ 70)</option>
                <option value="MEDIUM_RELEVANCE">Pertinence Moyenne (50-69)</option>
                <option value="UNCERTAIN">À Examiner (35-49)</option>
                <option value="LOW_RELEVANCE">Faible Pertinence</option>
              </select>

              {/* Filtre Statut Commercial */}
              <select
                className="ao-filter-select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setTimeout(() => fetchOpportunities(1), 0);
                }}
              >
                <option value="">Tous les Statuts</option>
                <option value="NEW">Nouveau</option>
                <option value="TO_REVIEW">À examiner</option>
                <option value="INTERESTED">Intéressé</option>
                <option value="PREPARING">En préparation</option>
                <option value="SUBMITTED">Soumissionné</option>
                <option value="WON">Gagné</option>
                <option value="LOST">Perdu</option>
                <option value="NOT_INTERESTED">Sans suite</option>
              </select>

              {/* Filtre Type */}
              <select
                className="ao-filter-select"
                value={tenderType}
                onChange={(e) => {
                  setTenderType(e.target.value);
                  setTimeout(() => fetchOpportunities(1), 0);
                }}
              >
                <option value="">Tous les Types</option>
                <option value="AO">Appel d'Offres (AO)</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="PROJET">Projet Programmé</option>
              </select>

              {/* Toggle Favoris */}
              <button
                type="button"
                className={`ao-fav-filter-btn ${favoritesOnly ? 'active' : ''}`}
                onClick={() => {
                  setFavoritesOnly((prev) => !prev);
                  setTimeout(() => fetchOpportunities(1), 0);
                }}
              >
                <Star size={14} className={favoritesOnly ? 'fill-amber-400 text-amber-400' : 'text-gray-400'} />
                <span>Favoris</span>
              </button>

              {/* Bouton Réinitialiser */}
              {hasActiveFilters && (
                <button
                  type="button"
                  className="ao-reset-filters-btn"
                  onClick={handleResetFilters}
                  title="Réinitialiser tous les filtres"
                >
                  <X size={13} className="mr-1" />
                  <span>Effacer filtres</span>
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ─── 5. Tableau des Opportunités de Marchés ─── */}
        <div className="ao-table-card">
          <div className="ao-table-header">
            <div className="flex items-center space-x-2.5">
              <h3 className="ao-table-title">Marchés Publics & Appels d'Offres</h3>
              <span className="ao-table-counter">
                {pagination.total} opportunité{pagination.total > 1 ? 's' : ''}
              </span>
            </div>
            <div className="ao-table-subhint">
              Source officielle TUNEPS synchronisée
            </div>
          </div>

          {loading ? (
            <div className="ao-loading-state">
              <RotateCw size={32} className="animate-spin text-amber-500 mb-3" />
              <p className="font-semibold text-gray-700">Chargement des marchés publics...</p>
              <p className="text-xs text-gray-400">Application des filtres et calcul des scores</p>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="ao-empty-box">
              <FileText size={48} className="text-gray-300 mb-3" />
              <h4 className="font-bold text-gray-700 text-base mb-1">Aucun appel d'offres en catalogue pour le moment</h4>
              <p className="text-xs text-gray-500 max-w-lg mx-auto mb-4 leading-relaxed">
                Les collectes en mode <strong>DRY_RUN</strong> ou <strong>DISCOVERY</strong> auditent les marchés sans persister en base.
                Pour inscrire définitivement les opportunités qualifiées dans ce tableau, lancez une collecte en mode <strong className="text-emerald-700">INGEST</strong> ou consultez les scans récents dans l'Historique.
              </p>
              <div className="flex items-center justify-center space-x-3">
                <button
                  type="button"
                  className="ao-btn ao-btn-outline ao-btn-sm"
                  onClick={() => setHistoryOpen(true)}
                >
                  <History size={14} className="mr-1.5 text-amber-500" />
                  <span>Voir les marchés découverts (Historique)</span>
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="ao-btn ao-btn-primary ao-btn-sm"
                    onClick={handleResetFilters}
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="ao-table-wrapper">
              <table className="ao-table">
                <thead>
                  <tr>
                    <th style={{ width: '48px', textAlign: 'center' }}>Fav</th>
                    <th style={{ width: '150px' }}>N° & Source</th>
                    <th>Intitulé & Synthèse IA</th>
                    <th style={{ width: '220px' }}>Acheteur & Région</th>
                    <th style={{ width: '150px' }}>Date Limite</th>
                    <th style={{ width: '135px' }}>Score IA</th>
                    <th style={{ width: '160px' }}>Statut Commercial</th>
                    <th style={{ width: '100px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp) => (
                    <tr key={opp._id} className="ao-table-row">
                      {/* Favori */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="ao-star-btn"
                          onClick={() => handleToggleFav(opp._id)}
                          title={opp.isFavorite ? 'Retirer des favoris' : 'Marquer comme favori'}
                        >
                          <Star
                            size={18}
                            className={opp.isFavorite ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                          />
                        </button>
                      </td>

                      {/* N° Marché & Source */}
                      <td>
                        <div className="ao-bid-no">{opp.bidNo}</div>
                        <div className="ao-bid-badges">
                          <span className="ao-source-badge">{opp.source || 'TUNEPS'}</span>
                          <span className="ao-type-badge">{opp.tenderType || 'AO'}</span>
                        </div>
                        {opp.bidModSeq && opp.bidModSeq !== '00' && (
                          <div className="ao-rectif-badge">
                            Rectificatif #{opp.bidModSeq}
                          </div>
                        )}
                      </td>

                      {/* Intitulé & Synthèse */}
                      <td>
                        <div className="ao-tender-title" title={opp.title}>
                          {opp.title}
                        </div>
                        {opp.titleAr && opp.titleAr !== opp.title && (
                          <div className="ao-tender-arabic" dir="rtl">
                            {opp.titleAr}
                          </div>
                        )}
                        {opp.aiSummary && (
                          <div className="ao-tender-ai-summary">
                            <span className="ao-ai-bulb">💡</span>
                            <span>{opp.aiSummary}</span>
                          </div>
                        )}
                      </td>

                      {/* Acheteur & Région */}
                      <td>
                        <div className="ao-buyer-name" title={opp.buyer}>
                          <Building size={13} className="text-gray-400 mr-1.5 flex-shrink-0" />
                          <span>{opp.buyer || 'Non précisé'}</span>
                        </div>
                        <div className="ao-region-tag">
                          <MapPin size={12} className="text-gray-400 mr-1 flex-shrink-0" />
                          <span>{opp.region || 'Toute la Tunisie'}</span>
                        </div>
                      </td>

                      {/* Date Limite */}
                      <td>
                        <div className="ao-deadline-date">
                          {formatDate(opp.submissionDeadline)}
                        </div>
                        <div className="mt-1">{getDeadlineBadge(opp.submissionDeadline)}</div>
                      </td>

                      {/* Score IA */}
                      <td>{getTierBadge(opp.aiTier, opp.aiScore)}</td>

                      {/* Statut Commercial */}
                      <td>
                        <select
                          className={`ao-status-select-pill ${String(opp.internalStatus || 'NEW').toLowerCase()}`}
                          value={opp.internalStatus || 'NEW'}
                          onChange={(e) => handleStatusChange(opp._id, e.target.value)}
                        >
                          <option value="NEW">Nouveau</option>
                          <option value="TO_REVIEW">À examiner</option>
                          <option value="INTERESTED">Intéressé</option>
                          <option value="PREPARING">En préparation</option>
                          <option value="SUBMITTED">Soumissionné</option>
                          <option value="WON">Gagné</option>
                          <option value="LOST">Perdu</option>
                          <option value="NOT_INTERESTED">Sans suite</option>
                        </select>
                      </td>

                      {/* Action */}
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="ao-btn ao-btn-primary ao-btn-sm"
                          onClick={() => setSelectedTender(opp)}
                          title="Consulter le dossier d'appel d'offres"
                        >
                          <Eye size={13} className="mr-1" />
                          <span>Dossier</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ─── 6. Pagination Sastra Standard ─── */}
          {pagination.pages > 1 && (
            <div className="ao-pagination-bar">
              <span className="ao-pagination-info">
                Affichage de la page <strong>{pagination.page}</strong> sur <strong>{pagination.pages}</strong> ({pagination.total} marchés au total)
              </span>
              <div className="ao-pagination-controls">
                <button
                  type="button"
                  className="ao-pagination-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchOpportunities(pagination.page - 1)}
                >
                  Précédent
                </button>
                <div className="ao-pagination-current-pill">
                  {pagination.page} / {pagination.pages}
                </div>
                <button
                  type="button"
                  className="ao-pagination-btn"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => fetchOpportunities(pagination.page + 1)}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Modal Détail du Marché ─── */}
      {selectedTender && (
        <TenderDetailModal
          tender={selectedTender}
          onClose={() => setSelectedTender(null)}
          onUpdated={() => {
            fetchMetrics();
            fetchOpportunities(pagination.page);
          }}
        />
      )}

      {/* ─── Modal Historique des Runs ─── */}
      {historyOpen && <TenderRunHistoryModal onClose={() => setHistoryOpen(false)} />}

      {/* ─── Modal Alertes Marchés ─── */}
      {alertsOpen && (
        <TenderAlertsModal
          onClose={() => {
            setAlertsOpen(false);
            fetchUnreadAlertsCount();
          }}
          onSelectTender={(tId) => handleOpenTenderDetailById(tId)}
        />
      )}
    </div>
  );
}
